import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { statusQuery } from "../../api";
import { ErrorBoundary } from "../../ErrorBoundary";

/**
 * Diagnoseseite: Zeigt, ob und in welcher Umgebung das Backend läuft. Taucht
 * bewusst in keiner Navigation auf — sie ist zum Nachsehen, wenn etwas klemmt.
 */
export const Route = createFileRoute("/_angemeldet/status")({
	component: StatusSeite,
});

function StatusSeite() {
	return (
		<>
			<header className="mb-6">
				<h1 className="font-display text-display font-medium text-ink tracking-tight">
					Systemstatus
				</h1>
				<p className="mt-1 text-sm text-ink-mute">Antwortet das Backend, und mit welchem Stand?</p>
			</header>

			<ErrorBoundary fallback={(error) => <StatusFehler error={error} />}>
				<Suspense fallback={<StatusSkeleton />}>
					<StatusKarte />
				</Suspense>
			</ErrorBoundary>
		</>
	);
}

/**
 * `useSuspenseQuery` statt eines Effekts: Der Request startet beim Rendern,
 * die Suspense-Grenze zeigt so lange das Skeleton. Kein useEffect nötig.
 */
function StatusKarte() {
	const { data: status } = useSuspenseQuery(statusQuery);

	return (
		<section className="card p-5 sm:p-6">
			<div className="flex items-center gap-2">
				<span className="size-2.5 rounded-full bg-positive" aria-hidden="true" />
				<h2 className="font-medium">Backend erreichbar</h2>
			</div>

			<dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
				<StatusFakt label="Umgebung" value={status.appEnv} />
				<StatusFakt label="Version" value={status.version} />
				<StatusFakt label="Laufzeit" value={`${String(status.uptimeSeconds)} s`} />
				<StatusFakt label="Serverzeit" value={status.timestamp} />
			</dl>
		</section>
	);
}

function StatusFakt(props: { label: string; value: string }) {
	return (
		<div className="min-w-0">
			<dt className="text-caption text-ink-mute">{props.label}</dt>
			<dd className="truncate font-mono text-sm">{props.value}</dd>
		</div>
	);
}

function StatusSkeleton() {
	return (
		<section className="card p-5 sm:p-6" aria-busy="true" aria-label="Status wird geladen">
			<div className="h-5 w-48 animate-pulse rounded bg-surface-2" />
			<div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
				{[0, 1, 2, 3].map((key) => (
					<div key={key}>
						<div className="h-3 w-20 animate-pulse rounded bg-surface-2" />
						<div className="mt-2 h-4 w-32 animate-pulse rounded bg-surface-2" />
					</div>
				))}
			</div>
		</section>
	);
}

function StatusFehler(props: { error: Error }) {
	return (
		<section className="rounded-2xl border border-negative/40 bg-negative/10 p-5 sm:p-6">
			<div className="flex items-center gap-2">
				<span className="size-2.5 rounded-full bg-negative" aria-hidden="true" />
				<h2 className="font-medium">Der Status konnte nicht geladen werden</h2>
			</div>
			<p className="mt-2 text-sm text-ink-mute">Diese Meldung bitte weitergeben:</p>
			<p className="mt-3 font-mono text-sm break-words text-ink">{props.error.message}</p>
		</section>
	);
}
